import { create } from 'zustand';
import { hasPermission, type ApiInfo, type CloudSite, type Permission, type Role } from '@ste/shared';

// The same UI runs on the gateway (one site, LAN) and on STE Connect (several sites, remote).
// Site-scoped API calls go to /api/v1 locally and to /api/v1/sites/{id} in the cloud.

export const API_ROOT: string = import.meta.env.VITE_API_BASE ?? '/api/v1';
const SITE_KEY = 'ste.siteId';

interface ConnectionState {
  mode: ApiInfo['mode'] | undefined;
  sites: CloudSite[];
  siteId: string | undefined;
  init(): Promise<void>;
  setSites(sites: CloudSite[]): void;
  selectSite(siteId: string): void;
  reset(): void;
}

function storedSite(): string | undefined {
  try {
    return localStorage.getItem(SITE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export const useConnection = create<ConnectionState>((set, get) => ({
  mode: undefined,
  sites: [],
  siteId: undefined,

  async init() {
    try {
      const res = await fetch(`${API_ROOT}/info`);
      const info = (await res.json()) as ApiInfo;
      set({ mode: info.mode });
    } catch {
      set({ mode: 'gateway' });
    }
  },

  setSites(sites) {
    const current = get().siteId ?? storedSite();
    const siteId = sites.find((s) => s.id === current)?.id ?? sites[0]?.id;
    set({ sites, siteId });
  },

  selectSite(siteId) {
    try {
      localStorage.setItem(SITE_KEY, siteId);
    } catch {
      // ignore
    }
    set({ siteId });
  },

  reset() {
    set({ sites: [], siteId: undefined });
  },
}));

/** base path for site-scoped calls */
export function siteBase(): string {
  const { mode, siteId } = useConnection.getState();
  if (mode === 'cloud') return siteId ? `${API_ROOT}/sites/${siteId}` : `${API_ROOT}/sites/_none`;
  return API_ROOT;
}

export function useCurrentSite(): CloudSite | undefined {
  return useConnection((s) => s.sites.find((site) => site.id === s.siteId));
}

/** effective role on the current site (cloud: membership, gateway: account role) */
export function roleOnSite(
  mode: ApiInfo['mode'] | undefined,
  site: CloudSite | undefined,
  accountRole: Role | undefined,
): Role | undefined {
  return mode === 'cloud' ? site?.role : accountRole;
}

export function can(role: Role | undefined, permission: Permission): boolean {
  return role !== undefined && hasPermission(role, permission);
}
